/**
 * @fileoverview Present matter-scoped recorded client verification and open its portal-result context. Backend review records own verification state; URL selection connects the badge to `PaymentResultModal.js`. A verification signifier is distinct from permission to reveal banking data or a new identity check.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import PaymentResultModal from './PaymentResultModal';
import { bankingVerificationStatus, BANKING_REQUEST_CREATED } from './verificationTiers';
import './ClientVerificationBadge.css';



export default function ClientVerificationBadge({ matterNumber, clientId, clientName }) {
  // Reset the modal and fetched data synchronously when the MatterClient changes.
  return <ScopedVerificationBadge key={`${matterNumber}:${clientId}`} matterNumber={matterNumber} clientId={clientId} clientName={clientName} />;
}



function ScopedVerificationBadge({ matterNumber, clientId, clientName }) {
  const [review, setReview] = useState(null);
  const [visible, setVisible] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const button = useRef(null);
  const [search] = useSearchParams();
  const linkedResult = search.get('payment_result');

  useEffect(

  () => {
    let cancelled = false;
    if (linkedResult) setVisible(false);
    if (!matterNumber || !clientId) return undefined;
    api.get(`/matters/${encodeURIComponent(matterNumber)}/payment-reviews`).then(({ data }) => {
      // The API orders newest first. Do not fall back to an older verification
      // when the latest request is pending, revoked, or rejected.
      const latest = data.clients?.find(group => String(group.client_id) === String(clientId))?.reviews[0];
      if (!cancelled) setReview(latest || null);
    }).catch(() => { if (!cancelled) setReview(null); });
    return () => { cancelled = true; };
  }, [matterNumber, clientId, refresh, linkedResult]);

  useEffect(() => {
    function refreshRequest() { setVisible(false); setRefresh(value => value + 1); }
    window.addEventListener(BANKING_REQUEST_CREATED, refreshRequest);
    return () => window.removeEventListener(BANKING_REQUEST_CREATED, refreshRequest);
  }, []);
  const status = bankingVerificationStatus(review);
  const label = `${status.label}${review?.test_mode ? ' · Sandbox' : ''}`;
  const canOpen = review && (['awaiting_submission', 'awaiting_client'].includes(review.status)
    || (['submitted', 'accepted', 'superseded'].includes(review.status) && review.submission?.identity?.status === 'verified'));
  

  function close() {
    setVisible(false);
    button.current?.focus();
    setRefresh(value => value + 1);
  }

  return <>
    {canOpen ? <button ref={button} type="button" className={`client-verification-badge${review.test_mode || !status.verified ? ' client-verification-badge--sandbox' : ''}`}
      aria-label={`${label} — view results for ${clientName}`}
      aria-haspopup="dialog" onClick={() => setVisible(true)}>
      <AppIcon icon={status.verified ? 'pi pi-shield' : 'pi pi-clock'} aria-hidden="true" />
      <span>{label}</span>
      <AppIcon icon="pi pi-angle-right" aria-hidden="true" />
    </button> : null}
    <PaymentResultModal visible={visible && !!canOpen && !linkedResult} onClose={close} reviewId={review?.id} matterNumber={matterNumber} clientId={clientId} />
  </>;
}
